import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Breadcrumb } from 'ui/breadcrumb';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import { colors, fontSizes, spacing, WebLink } from 'ui/theme';
import { usePanelContext } from '../../contexts/Panel';
import { useUploadAssetsContext } from '../../contexts/UploadAssetsContext';
import { useTranslations } from '../../hooks/useTranslations';
import { DropZoneArea, PanelIntent } from '../../types';
import { AssetListEmptyState } from '../AssetListEmptyState';
import { DropZone } from '../DropZone';
import { UploadDetail } from './components/UploadDetail';
import { UploadList } from './components/UploadList';
import { DeleteImageProvider } from 'modules/creativeLibrary/contexts/DeleteImageOverlayContext';
import { UrlToFileInput } from '../common/UrlToFileInput';
import { DeleteImageOverlay } from './components/DeleteImageOverlay';
import { useCloseModalOverlayContext } from 'modules/creativeLibrary/contexts/CloseModalOverlayContext';

const UploadViewBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  row-gap: ${spacing.base};
`;

const ControlSection = styled.section`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.half};
  > *:first-child > div {
    margin-bottom: 0;
  }
`;

const StyledWebLink = styled(WebLink)`
  font-size: ${fontSizes.s12};
`;

export const testIds = {
  breadcrumb: 'cl-panel-upload-breadcrumb',
  remoteUrlLink: 'cl-panel-upload-remote-url-link',
  container: 'cl-panel-upload-container',
  panelLeft: 'cl-panel-upload-panel-left',
  panelRight: 'cl-panel-upload-panel-right',
  urlInputField: 'cl-panel-remote-url-url-input-field',
};

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex: 1;
  column-gap: ${spacing.base};
  padding-bottom: ${spacing.base};
`;

const Panel = styled.div`
  border-radius: ${spacing.half};
  border: 1px solid ${colors.grey15};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const PanelViewUpload = () => {
  const { t } = useTranslations();
  const { pendingImages, activeDropZone, clearPendingUploads } = useUploadAssetsContext();
  const { panelIntent, setPanelIntent } = usePanelContext();
  const { onCloseModal } = useCloseModalOverlayContext();
  const initialUploadPanelIntent = useRef<PanelIntent>(panelIntent);
  const hasImages = pendingImages.length > 0;
  const shouldShowUploadsLink =
    panelIntent === PanelIntent.ImportFromUrl &&
    (initialUploadPanelIntent.current === PanelIntent.Upload || pendingImages.length > 0);

  useEffect(() => {
    return () => {
      clearPendingUploads();
    };
  }, []);

  return (
    <DeleteImageProvider>
      <UploadViewBody>
        <ControlSection data-test={testIds.breadcrumb}>
          <Breadcrumb
            isButton
            breadcrumb={{
              content: shouldShowUploadsLink ? t('link.uploads') : t('link.creativeLibrary'),
              onClick: () => {
                if (shouldShowUploadsLink) {
                  setPanelIntent(PanelIntent.Upload);
                  return;
                }
                onCloseModal();
              },
            }}
            variant={PAGE_HEADER_VARIANTS.SECONDARY}
          />
          {panelIntent !== PanelIntent.ImportFromUrl && (
            <StyledWebLink
              data-test={testIds.remoteUrlLink}
              onClick={() => setPanelIntent(PanelIntent.ImportFromUrl)}
            >
              {t('menu.linkRemote')}
            </StyledWebLink>
          )}
        </ControlSection>
        {!hasImages &&
          initialUploadPanelIntent.current === PanelIntent.Upload &&
          panelIntent === PanelIntent.Upload && (
            <DropZone area={DropZoneArea.Panel}>
              <AssetListEmptyState isDragging={activeDropZone === DropZoneArea.Panel} />
            </DropZone>
          )}
        {hasImages && panelIntent === PanelIntent.Upload && (
          <Container data-test={testIds.container}>
            <Panel data-test={testIds.panelLeft}>
              <UploadList />
            </Panel>
            <Panel data-test={testIds.panelRight}>
              <UploadDetail />
            </Panel>
          </Container>
        )}
        {(panelIntent === PanelIntent.ImportFromUrl ||
          (!hasImages && initialUploadPanelIntent.current === PanelIntent.ImportFromUrl)) && (
          <div>
            <UrlToFileInput inputTestId={testIds.urlInputField} />
          </div>
        )}
        <DeleteImageOverlay />
      </UploadViewBody>
    </DeleteImageProvider>
  );
};
